import { useState } from "react";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { CardElement } from "@stripe/react-stripe-js";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import InputBase from "@mui/material/InputBase";
import Box from "@mui/material/Box";
import { ALERT_ICON_TYPE_ERROR } from "../../utils/constants";
import Typography from "@mui/material/Typography";
import i18next from "i18next";
import {
  ADD_CARD,
  EMAIL_PLACEHOLDER,
  ERROR,
  ERROR_DESCRIPTION_GENERIC,
  FIELD_EMAIL_VALID,
  NAME_PLACEHOLDER,
  QUANTITY_TO_PAY,
} from "../../i18n/keysTranslations";
import { useTheme } from "@mui/material";
import { useDispatchApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import { startCreateStripeSource } from "../../actions/payments";
import { REGEX_EMAIL } from "../../utils/regex";
import { SimpleAlert } from "../Alerts/Alerts";
import TextField from "../Form/TextField";
import NumberField from "../Form/NumberField";
import ContainerModalActions from "../Containers/ContainerModalActions";
import StripeInformationCollected from "./StripeInformationCollected";
import StripeSecurePayments from "./StripeSecurePayments";
import { BOX_SHADOW_THEME, INPUT_COLOR_BORDER } from "../../utils/colors";

import { LaunchError } from "../../utils/errors";

const StripePayForm = ({
  textButton = i18next.t(ADD_CARD),
  positionButton = "center",
  onSubmit = () => {},
  saveCard = true,
  setDefaultCard = false,
  amount: amountValue = "",
  onChangeAmount = () => {},
  isSpecificValue,
  lastText = "",
  externalValidation = false,
  sx = {},
  CustomComponent,
  CustomComponentDetailPayment,
}) => {
  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    cardComplete: false,
  });
  const [amount, setAmount] = useState(amountValue);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const theme = useTheme();

  const { t } = useTranslationApp();

  const onChange = (e) => {
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value,
    });
  };

  const dispatch = useDispatchApp();
  const elements = useElements();
  const stripe = useStripe();

  const onAddPaymentMethod = async (paymentMethodID) => {
    const response = await dispatch(
      startCreateStripeSource({
        paymentMethodID,
        setDefault: setDefaultCard,
        activatePlan: false,
      })
    );
    return response?.ok;
  };

  const onCreatePaymentMethod = async (e) => {
    try {
      e.preventDefault();
      if (!REGEX_EMAIL.test(formValues.email))
        return setError(t(FIELD_EMAIL_VALID));
      if (!stripe || !elements) return;
      setLoading(true);

      const paymentMethod = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement("card"),
        billing_details: {
          email: formValues.email,
          name: formValues.name,
        },
      });
      if (saveCard) {
        const response = await onAddPaymentMethod(
          paymentMethod.paymentMethod.id
        );
        if (!response) {
          setLoading(false);
          return;
        }
      }
      const response = await onSubmit({
        id: paymentMethod.paymentMethod.id,
        brand: paymentMethod.paymentMethod.card.brand,
        last4: paymentMethod.paymentMethod.card.last4,
      });
      if (response?.ok) {
        setLoading(false);
        return;
      }
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "onCreatePaymentMethod",
          capture: "exception",
        })
      );
      console.error("onCreatePaymentMethod failed", {
        code: error?.code || null,
        type: error?.type || null,
      });
      setLoading(false);
      SimpleAlert({
        title: t(ERROR),
        text: t(ERROR_DESCRIPTION_GENERIC, {
          message: error.message,
          code: error.code,
        }),
        icon: ALERT_ICON_TYPE_ERROR,
      });
    }
  };

  return (
    <Box sx={{ ...sx }}>
      <StripeSecurePayments />
      <Grid
        component="form"
        justifyContent="center"
        alignItems="center"
        container
        spacing={2}
        sx={{ width: "100%", ml: 0 }}
      >
        {error && (
          <Grid item xs={12} sx={{ pl: "0px !important" }}>
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error}
            </Alert>
          </Grid>
        )}
        {isSpecificValue && (
          <Box sx={{ mb: 2 }}>
            <TextField
              value={amount}
              label={t(QUANTITY_TO_PAY)}
              variant="filled"
              fullWidth
              InputProps={{
                inputComponent: NumberField,
              }}
              onChange={(e) => {
                setAmount(e.target.value);
                onChangeAmount(e.target.value);
              }}
            />
          </Box>
        )}
        {CustomComponent && CustomComponent}
        <Grid item xs={12} sx={{ pl: "0px !important" }}>
          <Box
            sx={{
              width: "100%",
              backgroundColor: "background.paper",
              px: 2,
              py: 1,
              borderRadius: 2,
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              boxShadow: BOX_SHADOW_THEME,
            }}
          >
            <InputBase
              sx={{ ml: 1, flex: 1, width: "100%" }}
              placeholder={t(NAME_PLACEHOLDER)}
              name="name"
              background="paperSecondary"
              inputProps={{ "aria-label": t(NAME_PLACEHOLDER) }}
              value={formValues.name}
              onChange={onChange}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ pl: "0px !important" }}>
          <Box
            sx={{
              width: "100%",
              backgroundColor: "background.paper",
              px: 2,
              py: 1,
              borderRadius: 2,
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              boxShadow: BOX_SHADOW_THEME,
            }}
          >
            <InputBase
              sx={{ ml: 1, flex: 1, width: "100%" }}
              background="paperSecondary"
              placeholder={t(EMAIL_PLACEHOLDER)}
              name="email"
              inputProps={{ "aria-label": t(EMAIL_PLACEHOLDER) }}
              value={formValues.email}
              onChange={onChange}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sx={{ pl: "0px !important" }}>
          <Box
            sx={{
              width: "100%",
              backgroundColor: "background.paper",
              px: 2,
              py: 2,
              borderRadius: 2,
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              boxShadow: BOX_SHADOW_THEME,
            }}
          >
            <CardElement
              options={{
                hidePostalCode: true,
                style: {
                  base: {
                    color: theme.palette.text.primary,
                  },
                },
              }}
              onChange={(event) => {
                if (event.complete) {
                  onChange({
                    target: {
                      name: "cardComplete",
                      value: true,
                    },
                  });
                } else {
                  onChange({
                    target: {
                      name: "cardComplete",
                      value: false,
                    },
                  });
                }
              }}
            />
          </Box>
          <StripeInformationCollected />
        </Grid>
        {CustomComponentDetailPayment && (
          <Grid item xs={12} sx={{ pl: "0px !important" }}>
            {CustomComponentDetailPayment}
          </Grid>
        )}
        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            justifyContent: positionButton,
            pl: "0px !important",
          }}
        >
          <ContainerModalActions
            hasPadding={false}
            propsPrimaryButton={{
              showButton: true,
              text: textButton,
              loading,
              onClick: onCreatePaymentMethod,
              disabled:
                !formValues.name ||
                !formValues.email ||
                !formValues.cardComplete ||
                externalValidation,
            }}
          />
        </Grid>
        {lastText.length > 0 && (
          <Grid
            item
            xs={12}
            sx={{
              pl: "0px !important",
            }}
          >
            <Typography variant="h6" align="center" sx={{ mt: 1 }}>
              {lastText}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default StripePayForm;
